import { useTranslate } from "@refinedev/core";
import { Col, notification, Row } from "antd";
import {
  ArrowLeftIcon,
  ArrowSquareOutIcon,
  CheckIcon,
  CopyIcon,
  EnvelopeSimpleIcon,
  FacebookLogoIcon,
  GraduationCapIcon,
  LinkedinLogoIcon,
  XLogoIcon,
} from "@phosphor-icons/react";
import { ProjectInstanceSettingsRequest } from "pages/media/types";
import { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useProjectInstance } from "../hooks/useProjectInstance";
import { usePublishUnpublish } from "../hooks/usePublishUnpublish";
import { ExperienceLabel, ExperienceOptions } from "../types";
import { ProjectExperienceSelection } from "./ProjectExperienceSelection";
import { ProjectShareTimeLimitComponent } from "./ProjectShareTimeLimitComponent";
import {
  ProjectSlugSelection,
  ProjectSlugSelectionRef,
} from "./ProjectSlugSelection";
import { PublishProvider, usePublishContext } from "./PublishContext";
import { QuotaUpgradeModal } from "components/QuotaUpgradeModal";
import { useClipboard } from "hooks/useClipboard/";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics/";
import { useOrganization } from "hooks/useOrganization/";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import useIsAdmin from "hooks/useIsAdmin";
import { useScormExportOrgIds } from "../hooks/useScormExportOrgIds";
import { getPublicationUrl } from "../../utils";
import { hasHeyGeniusDocTag } from "../helpers/helper";
import { ScormExportModal } from "./ScormExportModal";
import { radius } from "@libertify/ui";

interface IProjectDetails {
  projectId: string;
  instanceId: string;
  organizationId?: string;
}

interface ProjectPublishProps {
  projectDetails: IProjectDetails;
  onCancel?: () => void;
  isLive: boolean;
  publicationUrl: string;
}

type ViewMode = "simple" | "config";

const SAVED_MODE_TO_EXPERIENCE_TYPE: Record<
  string,
  "publicLink" | "pinViaEmail" | "passwordProtected"
> = {
  PublicLink: "publicLink",
  PinByEmail: "pinViaEmail",
  PasswordProtected: "passwordProtected",
};

const getSavedExperienceType = (mode?: string | null) =>
  (mode ? SAVED_MODE_TO_EXPERIENCE_TYPE[mode] : undefined) ?? null;

const ProjectPublishContent: React.FC<ProjectPublishProps> = ({
  projectDetails,
  onCancel,
  isLive,
  publicationUrl,
}) => {
  const t = useTranslate();
  const { trackEvent } = useAnalytics();
  const { paywallFlow } = useSoftPaywall();
  const { copy } = useClipboard();
  const { getOrganization } = useOrganization();

  const [view, setView] = useState<ViewMode>(isLive ? "simple" : "config");
  const [localPublicationUrl, setLocalPublicationUrl] =
    useState(publicationUrl);
  const [copied, setCopied] = useState(false);
  const [scormModalOpen, setScormModalOpen] = useState(false);
  // Whitelisted customer orgs get the SCORM export (list lives in SCORM.json
  // in the whats-new repo — rollout without a deploy); admins always see it
  // (e.g. to QA or generate packages on a customer's behalf).
  const isAdmin = useIsAdmin();
  const scormExportOrgIds = useScormExportOrgIds();
  const isScormExportEnabled =
    isAdmin || scormExportOrgIds.includes(projectDetails.organizationId ?? "");
  const [slug, setSlug] = useState<string | null>(null);
  const [availableSlug, setAvailableSlug] = useState<boolean>(false);
  const childRef = useRef<ProjectSlugSelectionRef>(null);

  // Keep localPublicationUrl in sync with prop
  useEffect(() => {
    if (publicationUrl) {
      setLocalPublicationUrl(publicationUrl);
    }
  }, [publicationUrl]);

  const handleCheck = () => {
    return childRef?.current?.handleCheck();
  };

  const handleSave = async () => {
    handleCheck()?.then(
      ({ isAvailable, slug }: { isAvailable: boolean; slug: string }) => {
        if (isAvailable) {
          handleLaunch(slug).catch((error) => {
            console.error("Error launching experience:", error);
          });
        } else {
          notification.error({
            message: t("media.components.ProjectPublish.launchError"),
          });
        }
      }
    );
  };

  const [experienceOptions, setExperienceOptions] = useState<ExperienceOptions>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    projectExperienceType,
    setProjectExperienceType,
    isEmailValid,
    isPasswordValid,
    expirationTime,
    password,
    emails,
    setIsEmailValid,
    setIsPasswordValid,
    setExpirationTime,
    setPassword,
    setEmails,
  } = usePublishContext();

  useEffect(() => {
    if (projectExperienceType !== "pinViaEmail") {
      setIsEmailValid(false);
      setEmails([]);
    }
    if (projectExperienceType !== "passwordProtected") {
      setIsPasswordValid(false);
      setPassword(null);
    }
  }, [
    projectExperienceType,
    setIsEmailValid,
    setIsPasswordValid,
    setEmails,
    setPassword,
  ]);

  const {
    project,
    media,
    updateSettings,
    updateAuthorizedEmails,
    isLoadingProject,
  } = useProjectInstance({
    projectId: projectDetails.projectId,
    instanceId: projectDetails.instanceId,
  });

  const savedExperienceType = getSavedExperienceType(media?.publication_mode);

  // Seed the selection once from the saved publication mode, after the project
  // has loaded. Falls back to the public link when the mode is empty/unknown.
  useEffect(() => {
    if (projectExperienceType === null && !isLoadingProject) {
      setProjectExperienceType(savedExperienceType ?? "publicLink");
    }
  }, [
    projectExperienceType,
    isLoadingProject,
    savedExperienceType,
    setProjectExperienceType,
  ]);

  const { publishUnpublish, showQuotaModal, dismissQuotaModal } =
    usePublishUnpublish(projectDetails.projectId, projectDetails.instanceId);

  // Ref to always access the latest publishUnpublish closure after re-renders
  const publishUnpublishRef = useRef(publishUnpublish);
  publishUnpublishRef.current = publishUnpublish;

  useEffect(() => {
    if (media?.instance_slug) {
      setSlug(media.instance_slug);
    } else {
      setSlug(null);
    }
  }, [media?.instance_slug]);

  useEffect(() => {
    setExperienceOptions({
      [ExperienceLabel.Video]: true,
      [ExperienceLabel.AnimatedDocument]:
        media?.include_document_animations ?? false,
      [ExperienceLabel.Chatbot]: media?.chat_mode !== "Disabled",
      [ExperienceLabel.Audio]: media?.media_generation_mode === "Audio",
      [ExperienceLabel.Podcast]: true,
      [ExperienceLabel.Quiz]: true,
      [ExperienceLabel.TextSummary]: true,
    });
    if (media?.expiration_time) {
      const date = new Date(media.expiration_time);
      setExpirationTime(date);
    }
  }, [media, setExpirationTime]);

  const isVideoShareSelected = useMemo(() => {
    return Object.entries(experienceOptions).some(
      ([key, value]) => key !== ExperienceLabel.Video && value === true
    );
  }, [experienceOptions]);

  const canLaunch = useMemo(() => {
    if (!isVideoShareSelected || projectExperienceType === null) {
      return false;
    }

    if (projectExperienceType === "pinViaEmail") {
      return isEmailValid;
    }

    if (projectExperienceType === "passwordProtected") {
      return isPasswordValid;
    }

    return true;
  }, [
    isVideoShareSelected,
    projectExperienceType,
    isEmailValid,
    isPasswordValid,
  ]);

  const handleLaunch = async (lastSlug: string) => {
    if (!canLaunch || !projectExperienceType) return;

    try {
      setIsSubmitting(true);

      // Unpublish first when already live so settings can be updated
      if (isLive) {
        await publishUnpublish();
        // Yield to allow React to re-render with updated instance status,
        // so publishUnpublishRef gets the fresh closure that will publish
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }

      const publicationModeMap: Record<
        string,
        "PublicLink" | "PinByEmail" | "PasswordProtected"
      > = {
        publicLink: "PublicLink",
        pinViaEmail: "PinByEmail",
        passwordProtected: "PasswordProtected",
      };

      const expirationTimeISO = expirationTime
        ? new Date(expirationTime).toISOString()
        : null;

      const settingsPayload: ProjectInstanceSettingsRequest = {
        instance_slug: lastSlug || null,
        include_document_animations:
          experienceOptions[ExperienceLabel.AnimatedDocument] ?? false,
        chat_mode: experienceOptions[ExperienceLabel.Chatbot]
          ? "FullChat"
          : "Disabled",
        publication_mode: publicationModeMap[projectExperienceType],
        expiration_time: expirationTimeISO,
        ...(projectExperienceType === "passwordProtected" && password
          ? { password }
          : {}),
      };

      await updateSettings(settingsPayload);

      if (projectExperienceType === "pinViaEmail" && emails.length > 0) {
        await updateAuthorizedEmails(emails);
      }

      // Publish (or re-publish after the unpublish above).
      // Use ref to get the latest closure with updated instance status.
      const { quotaExceeded } = await publishUnpublishRef.current();

      if (!quotaExceeded) {
        setView("simple");
      }

      // Compute URL from slug for immediate display
      const organization = getOrganization(projectDetails.organizationId ?? "");
      if (lastSlug && organization?.slug) {
        setLocalPublicationUrl(
          getPublicationUrl({
            projectId: projectDetails.projectId,
            publicationId: projectDetails.instanceId,
            instanceSlug: lastSlug,
            organizationSlug: organization.slug,
            isDocHeyGenius: hasHeyGeniusDocTag(project?.tags),
            isAudioOnly: media?.media_generation_mode === "Audio",
          })
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      console.error("Failed to publish experience:", errorMessage, error);
      notification.error({
        message: t("media.components.ProjectPublish.launchError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = () => {
    copy(localPublicationUrl, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    trackEvent(AnalyticsEvents.ProjectSharingLinkCopy, {
      project_id: projectDetails.projectId,
      instance_id: projectDetails.instanceId,
    });
    trackEvent(AnalyticsEvents.ProjectShareIntent, {
      project_id: projectDetails.projectId,
      instance_id: projectDetails.instanceId,
      method: "clipboard",
      paywall_flow: paywallFlow,
    });
  };

  const socialShare = (platform: string) => {
    const url = encodeURIComponent(localPublicationUrl);
    const text = encodeURIComponent(
      t("media.components.ProjectPublish.shareText")
    );
    const map: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x: `https://x.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${text}&body=${url}`,
    };
    window.open(map[platform], "_blank", "noopener,noreferrer");
    trackEvent(AnalyticsEvents.ProjectShareIntent, {
      project_id: projectDetails.projectId,
      instance_id: projectDetails.instanceId,
      method: platform,
      paywall_flow: paywallFlow,
    });
  };

  return (
    <>
      <BodySM key={view}>
        {view === "simple" ? (
          <SimpleViewWrapper>
            <SuccessBadge>
              <CheckIcon size={28} weight="bold" color="#fff" />
            </SuccessBadge>
            <TitleSM style={{ textAlign: "center" }}>
              {t("media.components.ProjectPublish.linkGenerated")}
            </TitleSM>
            <SubtitleSM style={{ textAlign: "center" }}>
              {t("media.components.ProjectPublish.yourExperienceIsReady")}
            </SubtitleSM>

            <UrlRowSM>
              <UrlInputSM readOnly value={localPublicationUrl} />
              <CopyBtnSM
                $copied={copied}
                onClick={handleCopy}
                aria-label={t("media.components.ProjectPublish.copyShareLink")}
              >
                {copied ? (
                  <>
                    <CheckIcon size={14} weight="bold" />
                    {t("media.components.ProjectPublish.copied")}
                  </>
                ) : (
                  <CopyIcon size={14} />
                )}
              </CopyBtnSM>
            </UrlRowSM>

            <OpenExperienceLinkSM
              href={localPublicationUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent(AnalyticsEvents.ProjectShareIntent, {
                  project_id: projectDetails.projectId,
                  instance_id: projectDetails.instanceId,
                  method: "open_experience",
                  paywall_flow: paywallFlow,
                });
              }}
            >
              <ArrowSquareOutIcon size={16} weight="bold" aria-hidden />
              {t("media.components.ProjectPublish.openExperience")}
            </OpenExperienceLinkSM>

            {isScormExportEnabled && (
              <OpenScormBtnSM
                type="button"
                onClick={() => setScormModalOpen(true)}
              >
                <GraduationCapIcon size={16} weight="bold" aria-hidden />
                {t("scorm.export.cta")}
              </OpenScormBtnSM>
            )}

            <DividerSM>
              {t("media.components.ProjectPublish.orShareVia")}
            </DividerSM>

            <SocialRowSM>
              <SocialBtnSM
                $color="#1877f2"
                onClick={() => socialShare("facebook")}
                title="Facebook"
              >
                <FacebookLogoIcon size={20} weight="fill" />
              </SocialBtnSM>
              <SocialBtnSM
                $color="#000000"
                onClick={() => socialShare("x")}
                title="X (Twitter)"
              >
                <XLogoIcon size={20} weight="fill" />
              </SocialBtnSM>
              <SocialBtnSM
                $color="#0a66c2"
                onClick={() => socialShare("linkedin")}
                title="LinkedIn"
              >
                <LinkedinLogoIcon size={20} weight="fill" />
              </SocialBtnSM>
              <SocialBtnSM
                $color="#6b7280"
                onClick={() => socialShare("email")}
                title="Email"
              >
                <EnvelopeSimpleIcon size={20} />
              </SocialBtnSM>
            </SocialRowSM>

            <AdvancedLinkSM onClick={() => setView("config")}>
              {t("media.components.ProjectPublish.advancedSettings")} →
            </AdvancedLinkSM>
          </SimpleViewWrapper>
        ) : (
          <>
            {isLive && (
              <BackBtnSM onClick={() => setView("simple")}>
                <ArrowLeftIcon size={16} weight="bold" />
                {t("buttons.back")}
              </BackBtnSM>
            )}
            <TitleSM>{t("media.components.ProjectPublish.title")}</TitleSM>
            <SubtitleSM>
              {t("media.components.ProjectPublish.changeAccessSettingsAnytime")}
            </SubtitleSM>

            <SectionLabelSM>
              {t("media.components.ProjectPublish.customUrlSection")}
            </SectionLabelSM>
            <Row>
              <Col span={24}>
                <ProjectSlugSelection
                  ref={childRef}
                  project={project}
                  media={media}
                  setSlug={setSlug}
                  setAvailableSlug={setAvailableSlug}
                  organizationId={projectDetails.organizationId}
                />
              </Col>
            </Row>

            <SectionLabelSM>
              {t("media.components.ProjectPublish.whoCanAccess")}
            </SectionLabelSM>
            <ProjectExperienceSelection />

            <SectionLabelSM style={{ marginTop: 12 }}>
              {t("media.components.ProjectPublish.availabilitySection")}
            </SectionLabelSM>
            <ProjectShareTimeLimitComponent />

            <ButtonRowSM>
              {isLive ? (
                <UnpublishBtnSM
                  type="button"
                  onClick={() => {
                    publishUnpublish().catch((error) => {
                      console.error("Error unpublishing:", error);
                    });
                    onCancel?.();
                  }}
                >
                  {t("media.components.ProjectPublish.unpublish")}
                </UnpublishBtnSM>
              ) : (
                <CancelBtnSM type="button" onClick={onCancel}>
                  {t("buttons.cancel")}
                </CancelBtnSM>
              )}
              <PublishBtnSM
                type="button"
                onClick={handleSave}
                disabled={!canLaunch || isSubmitting}
              >
                {isSubmitting
                  ? "..."
                  : isLive
                    ? t("media.components.ProjectPublish.save")
                    : t("media.components.ProjectPublish.publish")}
              </PublishBtnSM>
            </ButtonRowSM>
          </>
        )}
      </BodySM>
      <QuotaUpgradeModal
        open={showQuotaModal}
        onClose={() => {
          dismissQuotaModal();
          onCancel?.();
        }}
      />
      {isScormExportEnabled && (
        <ScormExportModal
          open={scormModalOpen}
          onClose={() => setScormModalOpen(false)}
          projectId={projectDetails.projectId}
          publicationId={projectDetails.instanceId}
          experienceTitle={project?.title || ""}
          // Classic player URL — this publish-flow modal keeps targeting the
          // legacy experience so existing SCORM packages/users are unaffected.
          // The NEW experience SCORM is a separate entry point on the
          // experience-v2 page (passes getPublicationUrl({ experienceV2: true })).
          playerLaunchUrl={localPublicationUrl}
        />
      )}
    </>
  );
};

export const ProjectPublish: React.FC<ProjectPublishProps> = (props) => {
  return (
    <PublishProvider>
      <ProjectPublishContent {...props} />
    </PublishProvider>
  );
};

// ─── Styled Components ───────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const FONT_INTER = '"Inter Variable", "Inter", sans-serif';

const BodySM = styled.div`
  padding: 28px 24px 24px;
  animation: ${fadeIn} 0.18s ease both;
`;

const SimpleViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

const SuccessBadge = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  margin-bottom: 16px;
`;

const TitleSM = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  margin: 0 0 6px;
`;

const SubtitleSM = styled.p`
  font-family: ${FONT_INTER};
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 24px;
`;

const UrlRowSM = styled.div`
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 12px;
  margin-bottom: 10px;
  overflow: hidden;
`;

const UrlInputSM = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #374151;
  padding: 11px 0;
  outline: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CopyBtnSM = styled.button<{ $copied: boolean }>`
  flex-shrink: 0;
  background: ${(p) => (p.$copied ? "#059669" : "#14b8a6")};
  color: white;
  border: none;
  border-radius: 7px;
  padding: 6px 14px;
  font-family: ${FONT_INTER};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
  white-space: nowrap;

  &:hover {
    background: ${(p) => (p.$copied ? "#059669" : "#0d9488")};
  }
`;

const OpenExperienceLinkSM = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 42px;
  margin-top: 8px;
  padding: 0 16px;
  border-radius: 10px;
  background: transparent;
  border: 1.5px solid #e5e7eb;
  color: #0d9488;
  font-family: ${FONT_INTER};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:hover {
    background: #f0fdfa;
    border-color: #14b8a6;
    color: #0f766e;
  }

  &:focus-visible {
    outline: 2px solid #14b8a6;
    outline-offset: 2px;
  }
`;

const OpenScormBtnSM = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 42px;
  margin-top: 8px;
  padding: 0 16px;
  border-radius: 10px;
  background: transparent;
  border: 1.5px solid #e5e7eb;
  color: #374151;
  font-family: ${FONT_INTER};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:hover {
    background: #f9fafb;
    border-color: #6b7280;
    color: #111827;
  }

  &:focus-visible {
    outline: 2px solid #6b7280;
    outline-offset: 2px;
  }
`;

const DividerSM = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  color: #d1d5db;
  font-family: ${FONT_INTER};
  font-size: 12px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #f3f4f6;
  }
`;

const SocialRowSM = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 24px;
`;

const SocialBtnSM = styled.button<{ $color: string }>`
  flex: 1;
  height: 40px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(p) => p.$color};
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: ${(p) => p.$color};
    background: ${(p) => p.$color}11;
  }
`;

const BackBtnSM = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  font-family: ${FONT_INTER};
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-bottom: 24px;
  transition: color 0.15s;

  &:hover {
    color: #111827;
  }
`;

const AdvancedLinkSM = styled.button`
  display: block;
  width: 100%;
  text-align: center;
  background: none;
  border: none;
  font-family: ${FONT_INTER};
  font-size: 13px;
  color: #14b8a6;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.15s;

  &:hover {
    color: #0d9488;
  }
`;

const SectionLabelSM = styled.div`
  font-family: ${FONT_INTER};
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const ButtonRowSM = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const CancelBtnSM = styled.button`
  margin-left: auto;
  padding: 8px 20px;
  border: none;
  border-radius: ${radius.pill}px;
  background: transparent;
  font-family: ${FONT_INTER};
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #111827;
  }
`;

const UnpublishBtnSM = styled.button`
  padding: 8px 20px;
  border: 1.5px solid #ef4444;
  border-radius: ${radius.pill}px;
  background: transparent;
  font-family: ${FONT_INTER};
  font-size: 14px;
  font-weight: 500;
  color: #ef4444;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: #fef2f2;
  }
`;

const PublishBtnSM = styled.button`
  padding: 8px 24px;
  border: none;
  border-radius: ${radius.pill}px;
  background: #14b8a6;
  color: white;
  font-family: ${FONT_INTER};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    opacity 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #0d9488;
  }
`;
